import React from 'react';
import {
  Box, Container, Grid, Typography, Button, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SecurityIcon from '@mui/icons-material/Security';
import { useLang } from '../context/LanguageContext';

const valueIcons = [SecurityIcon, EmojiEventsIcon, HandshakeIcon];

const SectionLabel = ({ children }) => (
  <Box display="flex" alignItems="center" gap={2} mb={2}>
    <Box sx={{ width: 40, height: 3, background: '#C8102E', flexShrink: 0 }} />
    <Typography sx={{
      fontFamily: '"Oswald", sans-serif',
      color: '#C8102E',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      fontSize: '0.78rem',
      fontWeight: 500,
    }}>
      {children}
    </Typography>
  </Box>
);

export default function About() {
  const { t } = useLang();
  const a = t.about;

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box id="about" sx={{ py: { xs: 7, md: 11 }, background: '#FFFFFF' }}>
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">

          {/* Image — stacks on top on mobile */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'relative', maxWidth: { xs: '100%', md: 'none' } }}>
              <Box sx={{
                width: '100%',
                paddingBottom: { xs: '70%', md: '115%' },
                background: '#1a1a1a',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80"
                  alt="Construction team at work"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'contrast(1.05)',
                  }}
                />
              </Box>
              {/* Badge — bottom right, no overflow clipping issues */}
              <Box sx={{
                position: 'absolute',
                bottom: { xs: -16, md: -20 },
                right: { xs: 8, md: -20 },
                background: '#C8102E',
                p: { xs: 2.5, md: 3 },
                textAlign: 'center',
                minWidth: { xs: 110, md: 130 },
              }}>
                <Typography sx={{
                  fontFamily: '"Oswald", sans-serif',
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  fontWeight: 700,
                  color: '#fff',
                  lineHeight: 1,
                }}>
                  10+
                </Typography>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: { xs: '0.62rem', md: '0.72rem' },
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  whiteSpace: 'pre-line',
                }}>
                  {a.yearsLabel}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={7}>
            <SectionLabel>{a.eyebrow}</SectionLabel>

            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3, lineHeight: 1.15, whiteSpace: 'pre-line' }}>
              {a.heading}
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.9, mb: 2, fontWeight: 300 }}>
              {a.p1}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.9, mb: { xs: 3, md: 4 }, fontWeight: 300 }}>
              {a.p2}
            </Typography>

            {/* Values */}
            <Grid container spacing={2} mb={{ xs: 3, md: 4 }}>
              {a.values.map((v, i) => {
                const Icon = valueIcons[i];
                return (
                  <Grid item xs={12} sm={4} key={v.label}>
                    <Icon sx={{ color: '#C8102E', fontSize: 26, mb: 0.8 }} />
                    <Typography sx={{
                      fontFamily: '"Oswald", sans-serif',
                      fontWeight: 500,
                      fontSize: '0.92rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      mb: 0.5,
                    }}>
                      {v.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.85rem' }}>
                      {v.text}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>

            <Button variant="contained" color="secondary" size="large" onClick={scrollToContact}>
              {a.cta}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
